import { collaborationOnly } from '@yingbaba/utils';
import { CATALOG_PAGE_SIZE, catalogPage, catalogSearch } from './catalog-config';
import { reviewScore } from '@yingbaba/utils';
import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useList, useData, useSession, post, remove, orgID } from '@yingbaba/api';
import {
  Select,
  Avatar,
  Button,
  ButtonLink,
  ProviderCard,
  DemandCard,
  ResourceCard,
  CaseCard,
  PortfolioShowcase,
  QueryState,
  SectionHead,
  Image,
  categoryCover,
  marketplaceCover,
  Status,
  TagList,
  Sample,
  ActionDialog,
  Notice,
  ErrorNotice,
  Empty,
  Pagination,
  IconButton,
  useToast,
} from '@yingbaba/ui';
import {
  Search,
  ArrowRight,
  ArrowUpRight,
  MapPin,
  ShieldCheck,
  Heart,
  FileText,
  CheckCircle2,
  Clock,
} from '@yingbaba/icons';
import {
  categories,
  caseCategories,
  cityOptions,
  resourceCategories,
  money,
  date,
  siteURL,
  beijingToday,
} from '@yingbaba/utils';
import type {
  Demand,
  Provider,
  Resource,
  Portfolio,
  Review,
  ServicePackage,
  ServicePackageItem,
} from '@yingbaba/types';

type ServicePackageView = {
  package: ServicePackage;
  items: ServicePackageItem[];
};
type ProviderPerformance = {
  completed_projects: number;
  completed_amount_minor: number;
  average_rating: number;
  review_count: number;
};
type ProviderVerificationBadge = {
  type: string;
  verified_at: string;
};
const catalogLabels: Record<string, string> = {
  demands: '需求大厅',
  providers: '找服务商',
  resources: '影视资源',
  cases: '精选案例',
};
const catalogCopy: Record<string, string[]> = {
  demands: [
    '需求大厅',
    '每一次合作，都从一个好想法开始。发现值得投入的新项目。',
    'FIND YOUR NEXT PROJECT',
  ],
  providers: [
    '发现合拍的创作团队',
    '从作品认识风格，从专业建立信任。找到真正懂你的创作伙伴。',
    'MEET THE PEOPLE BEHIND THE FILM',
  ],
  resources: [
    '影视资源，一站找到',
    '好场景、好人才、好设备。让专业资源，为每一帧加分。',
    'EVERYTHING YOUR PRODUCTION NEEDS',
  ],
  cases: [
    '让好作品，带来新灵感',
    '探索不同类型、不同视角的影像表达，找到打动你的创作方向。',
    'STORIES THAT STAY WITH YOU',
  ],
};
export function Catalog({ kind }: { kind: string }) {
  const [params, setParams] = useSearchParams();
  const page = catalogPage(params.get('page'));
  const search = catalogSearch(params);
  const query = useList<Demand | Provider | Resource | Portfolio>(`/${kind}?${search}`);
  const [t, s, k] = catalogCopy[kind];
  const catalogCategories =
    kind === 'resources' ? resourceCategories : kind === 'cases' ? caseCategories : categories;
  function update(key: string, value: string) {
    setParams((p) => {
      if (value) p.set(key, value);
      else p.delete(key);
      p.delete('page');
      return p;
    });
  }
  return (
    <>
      <div className="page-hero catalog-hero">
        <div className="container catalog-hero-inner">
          <div className="catalog-heading-copy">
            <span className="eyebrow">{k}</span>
            <h1>{t}</h1>
            <p>{s}</p>
            {kind === 'resources' && (
              <Link className="link" to="/digital-resources">
                寻找视频、音乐或模板？前往数字素材库 <ArrowRight size={14} />
              </Link>
            )}
          </div>
          <form
            className="searchbox"
            role="search"
            aria-label={`搜索${catalogLabels[kind]}`}
            onSubmit={(e) => {
              e.preventDefault();
              update('q', String(new FormData(e.currentTarget).get('q') || '').trim());
            }}
          >
            <Search size={19} className="muted" />
            <input
              key={params.get('q') || 'all'}
              name="q"
              maxLength={200}
              aria-label="搜索关键词"
              defaultValue={params.get('q') || ''}
              placeholder={
                kind === 'demands' ? '搜索你感兴趣的项目、品牌或行业' : '搜索名称、创作方向…'
              }
            />
            <Button type="submit">搜索</Button>
          </form>
        </div>
      </div>
      <div className="container section catalog-section">
        <div className="catalog-category-rail" role="group" aria-label="快速筛选分类">
          {['', ...catalogCategories].map((category) => (
            <button
              key={category}
              type="button"
              aria-pressed={(params.get('category') || '') === category}
              onClick={() => update('category', category)}
            >
              {category || '全部类型'}
            </button>
          ))}
        </div>
        <div className="filter-bar catalog-filter-controls" data-has-city={kind !== 'cases'}>
          <Select
            aria-label="分类"
            value={params.get('category') || ''}
            onChange={(e) => update('category', e)}
          >
            <option value="">全部分类</option>
            {catalogCategories.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
          {kind !== 'cases' && (
            <Select
              aria-label="城市"
              value={params.get('city') || ''}
              onChange={(e) => update('city', e)}
            >
              <option value="">全部城市</option>
              {cityOptions.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          )}
          <Select
            className="catalog-sort"
            aria-label="排序"
            value={params.get('sort') || ''}
            onChange={(e) => update('sort', e)}
          >
            <option value="">最新发布</option>
            {kind === 'providers' && <option value="reputation">成交与口碑</option>}
            {kind === 'demands' && <option value="budget">预算由高到低</option>}
            {kind === 'resources' && <option value="price">价格由低到高</option>}
          </Select>
        </div>
        <QueryState query={query}>
          {query.data && !query.data.items.length && (
            <Empty
              title="暂未找到相关内容"
              description="可以换个关键词或分类，继续寻找合适的创作内容。"
              action={
                params.size > 0 ? (
                  <Button variant="secondary" onClick={() => setParams({})}>
                    浏览全部
                    {{ providers: '团队', demands: '需求', resources: '资源', cases: '案例' }[kind]}
                  </Button>
                ) : undefined
              }
            />
          )}
          <div className="grid-3">
            {query.data?.items.map((item) =>
              kind === 'providers' ? (
                <ProviderCard key={item.id} item={item as Provider} />
              ) : kind === 'demands' ? (
                <DemandCard key={item.id} item={item as Demand} />
              ) : kind === 'resources' ? (
                <ResourceCard key={item.id} item={item as Resource} />
              ) : (
                <CaseCard key={item.id} item={item as Portfolio} />
              ),
            )}
          </div>
        </QueryState>
        <Pagination
          page={page}
          total={query.data?.total || 0}
          size={CATALOG_PAGE_SIZE}
          onPage={(n) =>
            setParams((p) => {
              p.set('page', String(n));
              return p;
            })
          }
        />
      </div>
    </>
  );
}
type ProviderDetail = { provider: Provider; cases: Portfolio[]; reviews: Review[] };
type EngagementState = { favorite: boolean; following: boolean };
export function Detail({ kind }: { kind: string }) {
  const { id } = useParams();
  const query = useData<Demand | Resource | Portfolio | ProviderDetail>(`/${kind}/${id}`);
  const packages = useData<ServicePackageView[]>(`/providers/${id}/packages`, kind === 'providers');
  const performance = useData<ProviderPerformance>(
    `/providers/${id}/performance`,
    kind === 'providers' && !collaborationOnly,
  );
  const verifications = useData<ProviderVerificationBadge[]>(
    `/providers/${id}/verifications`,
    kind === 'providers',
  );
  const session = useSession();
  const engagement = useData<EngagementState>(`/engagement/${kind}/${id}`, Boolean(session.data));
  const navigate = useNavigate();
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  const [following, setFollowing] = useState(false);
  const item = query.data;
  const title = item
    ? kind === 'providers'
      ? (item as ProviderDetail).provider.name
      : (item as Demand | Resource | Portfolio).title
    : '';
  useEffect(() => {
    if (title) document.title = `${title} · 影爸爸`;
  }, [title]);
  useEffect(() => {
    if (!engagement.data) return;
    setSaved(engagement.data.favorite);
    setFollowing(engagement.data.following);
  }, [engagement.data]);
  async function favorite() {
    if (!session.data) {
      navigate('/login?returnTo=' + encodeURIComponent(location.pathname));
      return;
    }
    try {
      if (saved) {
        await remove(`/favorites/${kind}/${id}`);
        setSaved(false);
        toast('已取消收藏');
      } else {
        await post(`/favorites/${kind}/${id}`);
        setSaved(true);
        toast('已加入收藏，可在工作台查看');
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : '操作失败');
    }
  }
  async function followProvider() {
    if (!session.data) {
      navigate('/login?returnTo=' + encodeURIComponent(location.pathname));
      return;
    }
    try {
      if (following) {
        await remove(`/providers/${id}/follow`);
        setFollowing(false);
        toast('已取消关注');
      } else {
        await post(`/providers/${id}/follow`);
        setFollowing(true);
        toast('已关注团队');
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : '关注失败');
    }
  }
  return (
    <div className="container">
      <nav className="breadcrumb" aria-label="当前位置">
        <Link to="/">首页</Link>
        <span aria-hidden="true">/</span>
        <Link to={'/' + kind}>{catalogLabels[kind]}</Link>
        {title && (
          <>
            <span aria-hidden="true">/</span>
            <span aria-current="page" title={title}>
              {title}
            </span>
          </>
        )}
      </nav>
      <QueryState query={query}>
        {item &&
          (() => {
            const provider = kind === 'providers' ? (item as ProviderDetail).provider : null;
            const d = item as Demand;
            const resource = item as Resource;
            const portfolio = item as Portfolio;
            return (
              <>
                <div className="detail-heading">
                  {kind === 'cases' && <PortfolioShowcase key={portfolio.id} item={portfolio} />}
                  {(provider || kind === 'resources') && (
                    <Image
                      className="detail-cover"
                      src={marketplaceCover(kind, provider || resource)}
                      fallbackSrc={categoryCover(kind, provider?.category || resource.category)}
                      alt={title}
                      eager
                    />
                  )}
                  <div className="row spread">
                    <div className="row">
                      <Status
                        value={provider?.status || d.status}
                        label={
                          kind === 'cases'
                            ? '作品展示'
                            : kind === 'resources'
                              ? '联系发布人'
                              : undefined
                        }
                      />
                      <Sample show={provider?.is_sample || d.is_sample} />
                    </div>
                    <div className="row">
                      {kind === 'providers' && (
                        <Button
                          variant={following ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={followProvider}
                        >
                          {following ? '已关注' : '关注团队'}
                        </Button>
                      )}
                      <IconButton label={saved ? '取消收藏' : '收藏'} onClick={favorite}>
                        <Heart size={18} fill={saved ? 'currentColor' : 'none'} />
                      </IconButton>
                    </div>
                  </div>
                  <div className="row">
                    {provider && (
                      <Avatar
                        name={provider.name}
                        src={provider.avatar_url}
                        size={56}
                        organization
                      />
                    )}
                    <h1>{title}</h1>
                  </div>
                  <TagList value={provider?.tags || d.tags || d.category} />
                  {kind === 'cases' && portfolio.provider_id && (
                    <div className="case-byline">
                      <span>制作团队</span>
                      <Link to={`/providers/${portfolio.provider_id}`}>
                        <Avatar
                          name={portfolio.provider_name || '团队'}
                          src={portfolio.provider_avatar_url}
                          size={28}
                          organization
                        />{' '}
                        {portfolio.provider_name || '查看团队'}
                        <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  )}
                  {provider && verifications.data?.length ? (
                    <div className="row wrap" style={{ marginTop: 12 }}>
                      {verifications.data.map((v) => (
                        <span className="tag" key={v.type}>
                          <ShieldCheck size={13} />
                          {v.type === 'business'
                            ? '企业主体已核验'
                            : v.type === 'portfolio'
                              ? '作品真实性已核验'
                              : '专业信息已核验'}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {kind === 'demands' && (
                    <>
                      <div className="market-signals detail-trust-signals">
                        <span>
                          <ShieldCheck size={14} /> {d.client_name || '主体已认证'}
                        </span>
                        <span>
                          <CheckCircle2 size={14} /> {collaborationOnly ? '合作沟通' : '平台交易'}
                        </span>
                        <span>{d.candidate_count || 0} 家团队参与</span>
                        {!collaborationOnly && d.client_review_count > 0 && (
                          <span>
                            客户信用 {(d.client_rating_tenths / 10).toFixed(1)} ·{' '}
                            {d.client_review_count} 条评价
                          </span>
                        )}
                      </div>
                      <div className="detail-facts">
                        <div>
                          <span>项目预算</span>
                          <strong className="text-accent">
                            {money(d.budget_min_minor)} – {money(d.budget_max_minor)}
                          </strong>
                        </div>
                        <div>
                          <span>拍摄城市</span>
                          <strong>{d.city}</strong>
                        </div>
                        <div>
                          <span>预计交付</span>
                          <strong>{date(d.deadline)}</strong>
                        </div>
                        <div>
                          <span>项目周期</span>
                          <strong>{d.duration || '双方协商'}</strong>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className={`detail-grid detail-grid-${kind}`}>
                  <div>
                    <section className="detail-section" style={{ marginTop: 0 }}>
                      <h2>
                        {provider
                          ? '关于团队'
                          : kind === 'demands'
                            ? '项目需求'
                            : kind === 'resources'
                              ? '资源介绍'
                              : '创作说明'}
                      </h2>
                      <p className="prose">{provider?.description || d.description}</p>
                    </section>
                    {kind === 'demands' && (
                      <section className="detail-section">
                        <h2>交付要求</h2>
                        <p className="prose">{d.deliverables || '在方案沟通中进一步确认'}</p>
                      </section>
                    )}
                    {kind === 'demands' && !collaborationOnly && (
                      <section className="detail-section">
                        <div className="row spread wrap">
                          <div>
                            <h2>客户合作信用</h2>
                            <p className="small muted" style={{ marginTop: 8 }}>
                              仅统计影爸爸平台内已完成项目、真实合作评价和有明确到期时间的已确认付款。
                            </p>
                          </div>
                          <span className="tag">双边合作信用</span>
                        </div>
                        <div className="detail-facts reputation-facts">
                          <div>
                            <span>已完成合作</span>
                            <strong>{d.client_completed_projects || 0}</strong>
                          </div>
                          <div>
                            <span>合作评价</span>
                            <strong>
                              {d.client_review_count > 0
                                ? `${(d.client_rating_tenths / 10).toFixed(1)} / 5`
                                : '暂无'}
                            </strong>
                          </div>
                          <div>
                            <span>付款及时率</span>
                            <strong>
                              {d.client_payment_sample_count > 0
                                ? `${Math.round(d.client_on_time_payment_rate_bps / 100)}%`
                                : '暂无样本'}
                            </strong>
                          </div>
                          <div>
                            <span>付款样本</span>
                            <strong>{d.client_payment_sample_count || 0}</strong>
                          </div>
                        </div>
                      </section>
                    )}
                    {kind === 'cases' && portfolio.credits?.length > 0 && (
                      <section className="detail-section">
                        <div className="row spread wrap">
                          <div>
                            <h2>制作团队与资源</h2>
                            <p className="small muted" style={{ marginTop: 8 }}>
                              从作品继续找到参与创作的人与资源。
                            </p>
                          </div>
                        </div>
                        <div className="credit-grid">
                          {portfolio.credits.map((credit) => {
                            const body = (
                              <>
                                <span>{credit.role}</span>
                                <strong>{credit.display_name}</strong>
                                {credit.verified && <small>平台关联已核验</small>}
                              </>
                            );
                            return credit.subject_path ? (
                              <Link
                                className="credit-card"
                                to={credit.subject_path}
                                key={credit.id}
                              >
                                {body}
                                <ArrowUpRight size={14} />
                              </Link>
                            ) : (
                              <div className="credit-card" key={credit.id}>
                                {body}
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    )}
                    {provider && (
                      <section className="detail-section">
                        <div className="service-packages-heading">
                          <div>
                            <h2>标准服务产品</h2>
                            <p className="muted small">
                              先了解服务范围、制作周期和作品，再沟通具体合作安排。
                            </p>
                          </div>
                        </div>
                        <QueryState query={packages}>
                          {packages.data?.length ? (
                            <div className="service-packages-grid">
                              {packages.data.map((entry) => {
                                const pkg = entry.package;
                                return (
                                  <article className="panel service-package-card" key={pkg.id}>
                                    <span className="eyebrow">{pkg.category}</span>
                                    <h3>{pkg.name}</h3>
                                    {!collaborationOnly && (
                                      <div className="demand-price service-package-price">
                                        {pkg.pricing_mode === 'custom'
                                          ? '按需求报价'
                                          : pkg.pricing_mode === 'fixed'
                                            ? money(pkg.price_min_minor)
                                            : pkg.pricing_mode === 'starting_from'
                                              ? `${money(pkg.price_min_minor)} 起`
                                              : `${money(pkg.price_min_minor)} – ${money(pkg.price_max_minor)}`}
                                      </div>
                                    )}
                                    <p className="prose service-package-summary">{pkg.summary}</p>
                                    <div className="row wrap small muted">
                                      <span>约 {pkg.estimated_days} 天</span>
                                      <span>拍摄 {pkg.shoot_days} 天</span>
                                      <span>{pkg.revision_limit} 轮修改</span>
                                    </div>
                                    <div className="service-package-scopes">
                                      {pkg.deliverables && (
                                        <div className="package-scope">
                                          <strong>标准交付</strong>
                                          <p>{pkg.deliverables}</p>
                                        </div>
                                      )}
                                      {pkg.crew_description && (
                                        <div className="package-scope">
                                          <strong>团队配置</strong>
                                          <p>{pkg.crew_description}</p>
                                        </div>
                                      )}
                                      {pkg.equipment_description && (
                                        <div className="package-scope">
                                          <strong>设备配置</strong>
                                          <p>{pkg.equipment_description}</p>
                                        </div>
                                      )}
                                      {pkg.rights_description && (
                                        <div className="package-scope">
                                          <strong>版权与使用</strong>
                                          <p>{pkg.rights_description}</p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="service-package-actions">
                                      <ButtonLink
                                        to={`/publish?package=${pkg.id}&provider=${provider.id}&city=${encodeURIComponent(provider.city || '')}`}
                                        variant="secondary"
                                        className="btn-wide"
                                      >
                                        以此服务发布需求
                                        <ArrowRight size={15} />
                                      </ButtonLink>
                                    </div>
                                  </article>
                                );
                              })}
                            </div>
                          ) : (
                            <Empty title="团队正在整理标准服务产品" />
                          )}
                        </QueryState>
                      </section>
                    )}
                    {provider && performance.data && (
                      <section className="detail-section">
                        <h2>真实履约数据</h2>
                        <div className="detail-facts">
                          <div>
                            <span>已完成项目</span>
                            <strong>{performance.data.completed_projects}</strong>
                          </div>
                          <div>
                            <span>已完成交易额</span>
                            <strong>{money(performance.data.completed_amount_minor)}</strong>
                          </div>
                          <div>
                            <span>平均评价</span>
                            <strong>
                              {performance.data.average_rating
                                ? performance.data.average_rating.toFixed(1) + ' / 5'
                                : '暂无'}
                            </strong>
                          </div>
                          <div>
                            <span>成交评价</span>
                            <strong>{performance.data.review_count || 0}</strong>
                          </div>
                        </div>
                      </section>
                    )}
                    {provider && (
                      <section className="detail-section">
                        <h2>作品与案例</h2>
                        <div className="grid-2">
                          {(item as ProviderDetail).cases.map((v) => (
                            <CaseCard key={v.id} item={v} />
                          ))}
                        </div>
                        {!(item as ProviderDetail).cases.length && (
                          <Empty title="团队正在整理作品" />
                        )}
                      </section>
                    )}
                    {provider && !collaborationOnly && (
                      <section className="detail-section">
                        <h2>合作评价</h2>
                        {(item as ProviderDetail).reviews.length ? (
                          (item as ProviderDetail).reviews.map((r) => (
                            <div className="review-card" key={r.id}>
                              <div className="row spread wrap">
                                <div className="row wrap">
                                  <strong className="review-score">{reviewScore(r)} / 5</strong>
                                  {r.verified_transaction && (
                                    <span className="tag">
                                      <CheckCircle2 size={13} /> 已完成平台交易
                                    </span>
                                  )}
                                </div>
                                <span className="small muted">
                                  {r.project_category || '商业影像项目'}
                                  {r.project_completed_at
                                    ? ` · ${date(r.project_completed_at)}`
                                    : ''}
                                </span>
                              </div>
                              <div className="review-dimensions">
                                <span>合作质量 {r.quality_rating || r.rating}</span>
                                <span>沟通 {r.communication_rating || r.rating}</span>
                                <span>履约 {r.timeliness_rating || r.rating}</span>
                                <span>专业 {r.professionalism_rating || r.rating}</span>
                              </div>
                              {r.tags && <TagList value={r.tags} />}
                              <p className="prose">{r.content}</p>
                              {r.reply && (
                                <blockquote className="prose">
                                  <strong>团队回应</strong>
                                  <p>{r.reply}</p>
                                </blockquote>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="muted">
                            暂时没有已完成项目的评价。你可以通过作品与方案了解团队。
                          </p>
                        )}
                      </section>
                    )}
                    {kind === 'cases' && portfolio.provider_id && (
                      <section className="detail-section case-team-panel">
                        <span className="eyebrow">FROM INSPIRATION TO COLLABORATION</span>
                        <h2>喜欢这个作品的方向？</h2>
                        <p className="muted">
                          可以引用这个案例发布需求，也可以继续了解制作团队和它的标准服务。
                        </p>
                        <div className="row wrap" style={{ marginTop: 18 }}>
                          <ButtonLink
                            to={
                              portfolio.service_package_id
                                ? `/publish?case=${portfolio.id}&package=${portfolio.service_package_id}&provider=${portfolio.provider_id}`
                                : `/publish?case=${portfolio.id}`
                            }
                          >
                            {portfolio.service_package_id ? '按同类服务发需求' : '我要拍类似效果'}
                            <ArrowRight size={15} />
                          </ButtonLink>
                          <ButtonLink
                            to={`/providers/${portfolio.provider_id}`}
                            variant="secondary"
                          >
                            查看制作团队
                            <ArrowUpRight size={15} />
                          </ButtonLink>
                        </div>
                      </section>
                    )}
                  </div>
                  <aside className="detail-aside panel">
                    <span className="eyebrow">LET'S MAKE IT HAPPEN</span>
                    <h3>
                      {kind === 'demands'
                        ? '对这个项目感兴趣？'
                        : provider
                          ? '和这个团队聊聊想法'
                          : kind === 'resources'
                            ? '为你的项目确认资源'
                            : '让灵感成为你的作品'}
                    </h3>
                    <p className="muted" style={{ fontSize: 13, margin: '13px 0 22px' }}>
                      {kind === 'demands'
                        ? '先读懂需求，再用一份清晰的方案，让客户看到你的专业。'
                        : '在平台记录需求与沟通，让合作的每一步都有据可依。'}
                    </p>
                    {kind === 'resources' ? (
                      <ResourceContact id={id!} loggedIn={!!session.data} />
                    ) : kind === 'cases' ? (
                      <div className="stack">
                        <ButtonLink
                          to={
                            portfolio.service_package_id && portfolio.provider_id
                              ? `/publish?case=${id}&package=${portfolio.service_package_id}&provider=${portfolio.provider_id}`
                              : `/publish?case=${id}`
                          }
                          className="btn-wide"
                        >
                          {portfolio.service_package_id ? '按同类服务发需求' : '我要拍类似效果'}
                          <ArrowRight size={16} />
                        </ButtonLink>
                        {portfolio.provider_id && (
                          <ButtonLink
                            to={`/providers/${portfolio.provider_id}`}
                            variant="secondary"
                            className="btn-wide"
                          >
                            查看制作团队
                            <ArrowUpRight size={16} />
                          </ButtonLink>
                        )}
                      </div>
                    ) : (
                      <ButtonLink
                        to={kind === 'demands' ? siteURL('work', `/demands/${id}`) : '/publish'}
                        className="btn-wide"
                      >
                        {kind === 'demands' ? '了解需求与沟通' : '发布拍片需求'}
                        <ArrowUpRight size={16} />
                      </ButtonLink>
                    )}
                    <hr className="divider" />
                    <div className="stack small muted">
                      <span className="row">
                        <ShieldCheck size={16} />
                        平台记录，合作透明
                      </span>
                      <span className="row">
                        <CheckCircle2 size={16} />
                        任务、素材与沟通集中管理
                      </span>
                      <span className="row">
                        <FileText size={16} />
                        项目文件与沟通集中管理
                      </span>
                    </div>
                  </aside>
                </div>
              </>
            );
          })()}
      </QueryState>
    </div>
  );
}

function ResourceContact({ id, loggedIn }: { id: string; loggedIn: boolean }) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  async function contact() {
    setBusy(true);
    setError(null);
    try {
      const c = await post<{ id: string }>(`/resources/${id}/conversation`, {});
      window.location.assign(
        siteURL(
          'work',
          `/messages?conversation=${c.id}&organization_id=${encodeURIComponent(orgID())}`,
        ),
      );
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack">
      <p className="small muted">
        联系资源发布人，直接说明使用需求。合作方式与交易安排由双方自行商定。
      </p>
      <ErrorNotice error={error} />
      {loggedIn ? (
        <Button loading={busy} onClick={contact}>
          联系资源方
          <ArrowRight size={16} />
        </Button>
      ) : (
        <ButtonLink to={'/login?returnTo=' + encodeURIComponent('/resources/' + id)}>
          登录后联系资源方
        </ButtonLink>
      )}
    </div>
  );
}
